/**
 * Panorama <-> screen projection for the Pannellum 360 viewer.
 *
 * Pannellum exposes screen -> (pitch, yaw) as `mouseEventToCoords` but has no public inverse. The
 * forward projection below is the rectilinear math of its private `renderHotSpot`
 * (node_modules/pannellum/src/js/pannellum.js), and `screenToYawPitch` is `mouseEventToCoords`
 * without the DOM event. Both work in the viewer's hotspot space, so a polygon stored in yaw/pitch
 * lines up with hotspots and survives any zoom, pan or screen size.
 *
 * Screen coordinates are CSS pixels relative to the top-left of the panorama canvas. The view roll
 * is assumed to be 0 (the app never sets one).
 */

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** Snapshot of the camera needed by the projections. */
export function getViewState(viewer) {
  const canvas = viewer.getRenderer().getCanvas();
  return {
    pitch: viewer.getPitch(),
    yaw: viewer.getYaw(),
    hfov: viewer.getHfov(),
    width: canvas.clientWidth,
    height: canvas.clientHeight,
  };
}

/** Wraps a yaw into (-180, 180]. */
export function normalizeYaw(yaw) {
  let y = ((yaw + 180) % 360 + 360) % 360 - 180;
  if (y === -180) y = 180;
  return y;
}

/**
 * (yaw, pitch) -> screen point. `visible` is false when the direction is behind the camera, in which
 * case x/y are meaningless.
 */
export function projectToScreen(point, view) {
  const hsPitchSin = Math.sin(point.pitch * D2R);
  const hsPitchCos = Math.cos(point.pitch * D2R);
  const viewPitchSin = Math.sin(view.pitch * D2R);
  const viewPitchCos = Math.cos(view.pitch * D2R);
  const yawDelta = (-point.yaw + view.yaw) * D2R;
  const yawCos = Math.cos(yawDelta);
  const z = hsPitchSin * viewPitchSin + hsPitchCos * yawCos * viewPitchCos;
  if (z <= 0) return { x: 0, y: 0, visible: false };

  const yawSin = Math.sin(yawDelta);
  const hfovTan = Math.tan(view.hfov * D2R / 2);
  const x = -view.width / hfovTan * yawSin * hsPitchCos / z / 2;
  const y = -view.width / hfovTan * (hsPitchSin * viewPitchCos - hsPitchCos * yawCos * viewPitchSin) / z / 2;
  return { x: x + view.width / 2, y: y + view.height / 2, visible: true };
}

/** Screen point -> (yaw, pitch). Same math as Pannellum's mouseEventToCoords. */
export function screenToYawPitch(x, y, view) {
  const nx = x / view.width * 2 - 1;
  const ny = (1 - y / view.height * 2) * view.height / view.width;
  const focal = 1 / Math.tan(view.hfov * D2R / 2);
  const s = Math.sin(view.pitch * D2R);
  const c = Math.cos(view.pitch * D2R);
  const a = focal * c - ny * s;
  const root = Math.sqrt(nx * nx + a * a);
  const pitch = Math.atan((ny * c + focal * s) / root) * R2D;
  const yaw = normalizeYaw(Math.atan2(nx / root, a / root) * R2D + view.yaw);
  return { yaw, pitch };
}

/**
 * Mean direction of the points on the unit sphere. Correct across the ±180° yaw seam, unlike an
 * average of the angles. Mirrors AvaInspectSnapsController.SphericalCentroid on the server.
 */
export function sphericalCentroid(points) {
  let x = 0;
  let y = 0;
  let z = 0;
  points.forEach((p) => {
    const yaw = p.yaw * D2R;
    const pitch = p.pitch * D2R;
    x += Math.cos(pitch) * Math.cos(yaw);
    y += Math.cos(pitch) * Math.sin(yaw);
    z += Math.sin(pitch);
  });
  return {
    yaw: Math.atan2(y, x) * R2D,
    pitch: Math.atan2(z, Math.sqrt(x * x + y * y)) * R2D,
  };
}

/** Yaw/pitch span of the points, with yaw measured around `center` so the seam does not matter. */
export function angularExtent(points, center) {
  let minYaw = Infinity;
  let maxYaw = -Infinity;
  let minPitch = Infinity;
  let maxPitch = -Infinity;
  points.forEach((p) => {
    const dYaw = normalizeYaw(p.yaw - center.yaw);
    minYaw = Math.min(minYaw, dYaw);
    maxYaw = Math.max(maxYaw, dYaw);
    minPitch = Math.min(minPitch, p.pitch);
    maxPitch = Math.max(maxPitch, p.pitch);
  });
  // Yaw degrees shrink towards the poles; scale to the angle actually seen on screen.
  const yawSpan = (maxYaw - minYaw) * Math.cos(center.pitch * D2R);
  return { yawSpan, pitchSpan: maxPitch - minPitch };
}

/** How much wider than the polygon the zoomed view is. */
const FIT_MARGIN = 1.8;

/**
 * Horizontal field of view that fits the polygon with a margin, for a viewport of the given size.
 * Clamped to [minHfov, maxHfov].
 */
export function fitHfov(points, center, width, height, minHfov, maxHfov) {
  const { yawSpan, pitchSpan } = angularExtent(points, center);
  const aspect = width && height ? width / height : 16 / 9;
  const needed = Math.max(yawSpan, pitchSpan * aspect) * FIT_MARGIN;
  return Math.min(maxHfov, Math.max(minHfov, needed));
}

// ---------------------------------------------------------------------------------------------
// Outlines partly behind the camera
// ---------------------------------------------------------------------------------------------

/** Near plane for clipping, as the cosine-like depth `z` of projectToScreen (~89.4° off-axis). */
const NEAR_Z = 0.01;

/** Camera-space direction (cx, cy right/down, cz forward) of a yaw/pitch for `view`. */
function toCameraSpace(point, view) {
  const hsPitchSin = Math.sin(point.pitch * D2R);
  const hsPitchCos = Math.cos(point.pitch * D2R);
  const viewPitchSin = Math.sin(view.pitch * D2R);
  const viewPitchCos = Math.cos(view.pitch * D2R);
  const yawDelta = (-point.yaw + view.yaw) * D2R;
  return {
    cx: -Math.sin(yawDelta) * hsPitchCos,
    cy: -(hsPitchSin * viewPitchCos - hsPitchCos * Math.cos(yawDelta) * viewPitchSin),
    cz: hsPitchSin * viewPitchSin + hsPitchCos * Math.cos(yawDelta) * viewPitchCos,
  };
}

function cameraToScreen(c, view) {
  const k = view.width / Math.tan(view.hfov * D2R / 2) / 2;
  return { x: view.width / 2 + k * c.cx / c.cz, y: view.height / 2 + k * c.cy / c.cz };
}

function clipPoint(a, b) {
  const t = (NEAR_Z - a.cz) / (b.cz - a.cz);
  return { cx: a.cx + t * (b.cx - a.cx), cy: a.cy + t * (b.cy - a.cy), cz: NEAR_Z };
}

/**
 * Screen outline of a yaw/pitch shape, clipped where it passes behind the camera, so a long snap
 * shows the part in front of the viewer instead of disappearing.
 * Returns a list of runs (arrays of { x, y }): one ring for a closed shape, or the visible stretches
 * of an open polyline.
 */
export function projectOutlineClipped(points, view, closed) {
  const cam = points.map((p) => toCameraSpace(p, view));
  if (closed) {
    // Sutherland-Hodgman against the near plane.
    const out = [];
    for (let i = 0; i < cam.length; i++) {
      const cur = cam[i];
      const prev = cam[(i + cam.length - 1) % cam.length];
      const curIn = cur.cz >= NEAR_Z;
      const prevIn = prev.cz >= NEAR_Z;
      if (curIn) {
        if (!prevIn) out.push(clipPoint(prev, cur));
        out.push(cur);
      } else if (prevIn) {
        out.push(clipPoint(prev, cur));
      }
    }
    return out.length >= 3 ? [out.map((c) => cameraToScreen(c, view))] : [];
  }

  const runs = [];
  let run = [];
  for (let i = 0; i < cam.length; i++) {
    const cur = cam[i];
    const prev = i > 0 ? cam[i - 1] : null;
    const curIn = cur.cz >= NEAR_Z;
    if (prev && (prev.cz >= NEAR_Z) !== curIn) {
      const edge = clipPoint(prev, cur);
      if (curIn) run = [edge];
      else {
        run.push(edge);
        runs.push(run);
        run = [];
      }
    }
    if (curIn) run.push(cur);
  }
  if (run.length) runs.push(run);
  return runs.filter((r) => r.length > 1).map((r) => r.map((c) => cameraToScreen(c, view)));
}

// ---------------------------------------------------------------------------------------------
// Horizon pose (levelled scans) and the flat equirectangular image
// ---------------------------------------------------------------------------------------------

function toVector(point) {
  const yaw = point.yaw * D2R;
  const pitch = point.pitch * D2R;
  return [Math.cos(pitch) * Math.cos(yaw), Math.cos(pitch) * Math.sin(yaw), Math.sin(pitch)];
}

function fromVector(v) {
  return {
    yaw: Math.atan2(v[1], v[0]) * R2D,
    pitch: Math.asin(Math.max(-1, Math.min(1, v[2]))) * R2D,
  };
}

/**
 * Rotation R with imageDir = R · viewerDir, read off libpannellum's render() (the pose block that
 * turns the view direction into the direction sampled from the image). `pose` holds the viewer's
 * horizonPitch / horizonRoll in DEGREES, as in its config.
 */
export function horizonRotation(pose) {
  const h = (pose?.horizonPitch || 0) * D2R;
  const r = (pose?.horizonRoll || 0) * D2R;
  const cH = Math.cos(h);
  const sH = Math.sin(h);
  const cR = Math.cos(r);
  const sR = Math.sin(r);
  return [
    [cH, sR * sH, cR * sH],
    [0, cR, -sR],
    [-sH, cH * sR, cR * cH],
  ];
}

function multiply(m, v) {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}

function multiplyTransposed(m, v) {
  return [
    m[0][0] * v[0] + m[1][0] * v[1] + m[2][0] * v[2],
    m[0][1] * v[0] + m[1][1] * v[1] + m[2][1] * v[2],
    m[0][2] * v[0] + m[1][2] * v[1] + m[2][2] * v[2],
  ];
}

/** Viewer (stored) yaw/pitch -> the image's own yaw/pitch. */
export function viewerToImage(point, rotation) {
  return fromVector(multiply(rotation, toVector(point)));
}

/** The image's own yaw/pitch -> viewer (stored) yaw/pitch. */
export function imageToViewer(point, rotation) {
  return fromVector(multiplyTransposed(rotation, toVector(point)));
}

/** Image yaw/pitch -> equirectangular pixel (x in [0, width), y in [0, height]). */
export function imageYawPitchToPixel(point, width, height) {
  const u = ((normalizeYaw(point.yaw) / 360 + 0.5) % 1 + 1) % 1;
  return { x: u * width, y: (0.5 - point.pitch / 180) * height };
}

/** Equirectangular pixel -> image yaw/pitch. x may be outside [0, width) (the image wraps). */
export function pixelToImageYawPitch(x, y, width, height) {
  const clampedY = Math.max(0, Math.min(height, y));
  return { yaw: normalizeYaw((x / width - 0.5) * 360), pitch: 90 - clampedY / height * 180 };
}

/** Longest step (degrees) of an outline edge in the image polygon; see imagePolygonOf. */
const IMAGE_POLYGON_STEP = 2;
/** The API accepts at most this many image polygon points (AvaInspectSnapsController.MaxPolygonPoints). */
const IMAGE_POLYGON_MAX_POINTS = 2000;

/**
 * A snap outline (viewer yaw/pitch, as stored) in the source image's own equirectangular space,
 * normalised 0..1 (x = image yaw, y = image pitch): what the corrosion analysis crops.
 *
 * Stored edges are great circles (that is how the 360 view draws them), which curve in the flat
 * image, so each edge is walked in small steps before the levelling is undone. `rotation` is the
 * horizonRotation() the snap was drawn with.
 */
export function imagePolygonOf(points, rotation) {
  const vectors = points.map((p) => multiply(rotation, toVector(p)));
  const count = vectors.length;
  const totalAngle = vectors.reduce((sum, v, i) => sum + vectorAngle(v, vectors[(i + 1) % count]), 0);
  // Every vertex is kept; the steps in between share what is left of the point budget.
  const step = Math.max(IMAGE_POLYGON_STEP, totalAngle / Math.max(1, IMAGE_POLYGON_MAX_POINTS - count));

  const out = [];
  vectors.forEach((a, i) => {
    const b = vectors[(i + 1) % count];
    const pieces = Math.max(1, Math.ceil(vectorAngle(a, b) / step));
    for (let k = 0; k < pieces; k++) out.push(slerp(a, b, k / pieces));
  });
  return out.map((v) => {
    const px = imageYawPitchToPixel(fromVector(v), 1, 1);
    return { x: Math.min(1, Math.max(0, px.x)), y: Math.min(1, Math.max(0, px.y)) };
  });
}

function vectorAngle(a, b) {
  const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  return Math.acos(Math.max(-1, Math.min(1, dot))) * R2D;
}

/** Point `t` of the way along the great circle from unit vector `a` to `b`. */
function slerp(a, b, t) {
  const omega = vectorAngle(a, b) * D2R;
  if (omega < 1e-9) return a;
  const s = Math.sin(omega);
  const wa = Math.sin((1 - t) * omega) / s;
  const wb = Math.sin(t * omega) / s;
  return [wa * a[0] + wb * b[0], wa * a[1] + wb * b[1], wa * a[2] + wb * b[2]];
}

/** Great-circle angle between two yaw/pitch directions, degrees. */
export function angularDistance(a, b) {
  const va = toVector(a);
  const vb = toVector(b);
  const dot = va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2];
  return Math.acos(Math.max(-1, Math.min(1, dot))) * R2D;
}

/**
 * Where to point the camera for a snap. The spherical mean is right for compact shapes, but for an
 * outline wrapping far around the viewer (a long pipe) the mean vector shrinks and its direction is
 * unreliable; then aim at the middle of the outline's yaw extent instead.
 */
export function snapAimPoint(points) {
  let x = 0;
  let y = 0;
  let z = 0;
  points.forEach((p) => {
    const v = toVector(p);
    x += v[0];
    y += v[1];
    z += v[2];
  });
  const meanLength = Math.sqrt(x * x + y * y + z * z) / Math.max(1, points.length);
  if (meanLength >= 0.5) return sphericalCentroid(points);

  // Unwrap yaw along the outline so a pipe passing the seam is one continuous range.
  let prev = points[0].yaw;
  let minYaw = prev;
  let maxYaw = prev;
  let pitchSum = 0;
  points.forEach((p) => {
    const unwrapped = prev + normalizeYaw(p.yaw - prev);
    minYaw = Math.min(minYaw, unwrapped);
    maxYaw = Math.max(maxYaw, unwrapped);
    pitchSum += p.pitch;
    prev = unwrapped;
  });
  return { yaw: normalizeYaw((minYaw + maxYaw) / 2), pitch: pitchSum / points.length };
}

/**
 * Largest round-trip error (px) of screen -> yaw/pitch -> screen over a grid of the viewport.
 * Used as a sanity check when a drawing session starts (logged, not thrown).
 */
export function projectionRoundTripError(view, steps = 20) {
  let worst = 0;
  for (let i = 0; i <= steps; i++) {
    for (let j = 0; j <= steps; j++) {
      const x = view.width * i / steps;
      const y = view.height * j / steps;
      const back = projectToScreen(screenToYawPitch(x, y, view), view);
      if (!back.visible) return Infinity;
      worst = Math.max(worst, Math.hypot(back.x - x, back.y - y));
    }
  }
  return worst;
}
